import React from 'react';
import { Box, Container, Typography, Stack, Link, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';

const TOOL_LINKS = [
  { label: 'Merge PDF', to: '/' },
  { label: 'Merge PDF & Images', to: '/merge-pdf-images' },
  { label: 'Create Blank PDF', to: '/create-pdf' },
  { label: 'Split PDF', to: '/split-pdf' },
  { label: 'Compress PDF', to: '/compress-pdf' },
  { label: 'Edit PDF', to: '/edit-pdf' },
  { label: 'Delete PDF Pages', to: '/delete-pages' },
  { label: 'Repair PDF', to: '/repair-pdf' },
  { label: 'PDF to Word', to: '/pdf-to-word' },
  { label: 'PDF to JPG', to: '/pdf-to-jpg' },
  { label: 'Word to PDF', to: '/word-to-pdf' },
  { label: 'HTML to PDF', to: '/html-to-pdf' },
  { label: 'PNG to PDF', to: '/png-to-pdf' },
  { label: 'Edit PDF Metadata', to: '/edit-metadata' },
  { label: 'Excel to PDF', to: '/excel-to-pdf' },
  { label: 'PowerPoint to PDF', to: '/powerpoint-to-pdf' },
  { label: 'Office to PDF', to: '/office-to-pdf' },
  { label: 'OCR PDF', to: '/ocr-pdf' },
  { label: 'Protect PDF', to: '/protect-pdf' },
  { label: 'Rotate PDF', to: '/rotate-pdf' },
  { label: 'Watermark PDF', to: '/watermark-pdf' },
];

export default function Footer() {
  const { t } = useTranslation();

  return (
    <Box
      component="footer"
      sx={{
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #1a1a2e 0%, #13132a 100%)'
          : 'linear-gradient(180deg, #fff 0%, #f8f7ff 100%)',
        borderTop: '1px solid rgba(99,102,241,0.1)',
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'center', md: 'flex-start' }}
          spacing={4}
          textAlign={{ xs: 'center', md: 'left' }}
        >
          {/* Brand */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Box component="img" src="/logo.png" alt="TG Edit-Pdf" sx={{ width: 34, height: 34, borderRadius: '9px', objectFit: 'contain' }} />
              <Typography variant="h6" fontWeight={700} fontFamily="Poppins, sans-serif">
                TG <Box component="span" sx={{ color: 'primary.main' }}>Edit-Pdf</Box>
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" maxWidth={240}>
              {t('footer.tagline')}
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={1.5}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', md: 'flex-start' }, maxWidth: 260 }}>
              <LockIcon sx={{ fontSize: 14, color: 'success.main' }} />
              {t('footer.privacy_badge')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1.5} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              {t('footer.made_by')}&nbsp;
              <Link
                href="https://www.takshagroups.in"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: 'primary.main', fontWeight: 600 }}
              >
                Taksha Groups
              </Link>
            </Typography>
          </Box>

          {/* Tools */}
          <Stack spacing={0.75} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={0.5}>{t('nav.tools')}</Typography>
            {TOOL_LINKS.map(({ label, to }) => (
              <Link key={to} component={RouterLink} to={to} underline="hover" color="text.secondary" variant="body2">{label}</Link>
            ))}
          </Stack>

          {/* Company */}
          <Stack spacing={0.75} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={0.5}>{t('footer.company')}</Typography>
            <Link component={RouterLink} to="/about" underline="hover" color="text.secondary" variant="body2">{t('nav.about')}</Link>
            <Link component={RouterLink} to="/contact" underline="hover" color="text.secondary" variant="body2">{t('nav.contact')}</Link>
            <Link component={RouterLink} to="/help" underline="hover" color="text.secondary" variant="body2">{t('nav.help')}</Link>
          </Stack>

          {/* Resources + Legal */}
          <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Stack spacing={0.75} alignItems={{ xs: 'center', md: 'flex-start' }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={0.5}>{t('footer.resources')}</Typography>
              <Link component={RouterLink} to="/blog" underline="hover" color="text.secondary" variant="body2">{t('nav.blog')}</Link>
              <Link component={RouterLink} to="/glossary" underline="hover" color="text.secondary" variant="body2">{t('nav.glossary')}</Link>
              <Link component={RouterLink} to="/api-docs" underline="hover" color="text.secondary" variant="body2">{t('footer.api')}</Link>
            </Stack>
            <Stack spacing={0.75} alignItems={{ xs: 'center', md: 'flex-start' }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={0.5}>{t('footer.legal')}</Typography>
              <Link component={RouterLink} to="/privacy" underline="hover" color="text.secondary" variant="body2">{t('footer.privacy')}</Link>
              <Link component={RouterLink} to="/terms" underline="hover" color="text.secondary" variant="body2">{t('footer.terms')}</Link>
            </Stack>
          </Stack>
        </Stack>

        <Divider sx={{ my: 4, borderColor: 'rgba(99,102,241,0.08)' }} />

        <Typography variant="caption" color="text.disabled" align="center" display="block">
          {/* One interpolated string (not text + {expr} + text): react-snap merges
              adjacent text nodes in its snapshot, so mixing literals and expressions
              breaks React 18 hydration (#418 → full-page remount). */}
          {`© ${new Date().getFullYear()} Taksha Groups. ${t('footer.rights')}`}
        </Typography>
      </Container>
    </Box>
  );
}
